import axios, { AxiosError, InternalAxiosRequestConfig } from "axios";
import { handleRequest } from "../../axios";
import {
    // Události
    ODPOVEDI_BY_POPLACH_ID_AND_TOKEN_URL,
    ODPOVEDI_BY_POPLACH_ID_URL,
    ODPOVEDI_POST_URL,
    REFRESH_PAGE,
    UDALOSTI_AKTIVNI_AUDIO_URL,
    UDALOSTI_AKTIVNI_URL,
    UDALOSTI_DEAKTIVOVAT_URL,
    UDALOSTI_AKTIVOVAT_URL,
    UDALOSTI_DELETE_URL,
    UDALOSTI_ALL_URL,
    UDALOSTI_DETAIL_URL,
    UDALOSTI_RESEND_NOTIFIKACE_URL,
    UDALOSTI_PRINT_STATUS_URL,
    UDALOSTI_PRINT_URL,
    UDALOSTI_PDF_URL,
    UDALOSTI_PDF_PREVIEW_URL,
    UDALOSTI_TEST_WEBPUSH_URL,
    UDALOSTI_TYPES_URL,
    UDALOSTI_AKTIVNI_AUDIO_BASE_URL,
    UDALOSTI_AKTIVNI_POSLEDNI_HODINA_URL,
    UDALOSTI_ROUTE_URL,
    UDALOSTI_TECHNIKA_URL,
    UDALOSTI_NOTIFIKACE_LOG_URL,
    // Konfigurace
    KONFIGURACE_ALL_URL,
    KONFIGURACE_BY_KEY_URL,
    APP_CONFIG_URL,
    // Device tokens
    DEVICE_TOKEN_CREATE_URL,
    DEVICE_TOKENS_LIST_URL,
    DEVICE_TOKEN_DELETE_URL,
    DEVICE_TOKEN_UPDATE_URL,
    DEVICE_TOKEN_THEME_URL,
    // Statistiky
    UDALOSTI_STATISTIKY_URL,
    STATISTIKA_POPLACHY_URL,
    STATISTIKA_BASE_URL,
    STATISTIKA_DATUM_URL,
    STATISTIKA_MOJE_URL,
    STATISTIKA_UZIVATEL_URL,
    STATISTIKA_TYM_GRAFY_URL,
    // Dashboard
    DASHBOARD_STATS_URL,
    // Reports
    REPORTS_SOUHRN_URL,
    REPORTS_SOUHRN_CLEN_URL,
    REPORTS_EXPORT_XLSX_URL,
    REPORTS_EXPORT_PDF_URL,
    REPORTS_DETAIL_URL,
    // Lokace
    LOKACE_SEARCH_URL,
    // Jednotka
    JEDNOTKA_URL,
    JEDNOTKA_LOGO_URL,
    // Poplach
    POPLACH_STATUS_URL,
    // Auth
    AUTH_REFRESH_URL,
} from "../url";
import { API_SERVER_URL } from "../../../resources/constants";
import type {
    Konfigurace,
    StatistikaPoplach,
    DashboardStats,
    UdalostStatistiky,
    PagedResponseUdalost,
    UdalostDetail,
    Udalost,
    AlertStatus,
    RefreshTokenResponse,
} from "../generated";
import type { StatistikaResponse } from "../generated/models/StatistikaResponse";
import type { JednotkaDto } from "../generated/models/JednotkaDto";
import type { SearchUdalostiParams } from "../generated/models/SearchUdalostiParams";
import type { LokaceSearchResult } from "../generated/models/LokaceSearchResult";
import type { CreateDeviceTokenRequest, DeviceTokenResponse, UpdateDeviceTokenRequest, DeviceTokenThemeResponse } from "../generated/models/DeviceToken";
import type { UdalostRouteResponse } from "../generated/models/UdalostRoute";
import type { OsobniStatistikaResponse } from "../generated/models/OsobniStatistika";
import type { TymoveGrafyResponse } from "../generated/models/TymoveGrafy";
import type { ReportSouhrn, ReportSouhrnClena } from "../generated/models/ReportSouhrn";
import type { ReportDetail } from "../generated/models/ReportDetail";
import type { AppConfigResponse } from "../generated/models/AppConfigResponse";
import { useAuthStore, getAccessToken, getRefreshToken } from "../../../store/auth-store";

const backendURL = API_SERVER_URL;

export const axiosInstance = axios.create({
    baseURL: backendURL,
    withCredentials: true,
    timeout: 10000,
});

// Axios instance for refresh token (without interceptors to avoid infinite loops)
const refreshAxiosInstance = axios.create({
    baseURL: backendURL,
    withCredentials: true,
    timeout: 10000,
});

// -----------------------------
// TOKEN REFRESH LOGIC
// -----------------------------

// Singleton promise pro token refresh - zabraňuje duplicitním refresh requestům
let refreshPromise: Promise<string> | null = null;


const clearAuthAndRedirect = () => {
    // Don't redirect if using deviceToken (public dashboard access)
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has('deviceToken')) {
        return;
    }

    // Clear auth state via Zustand store
    useAuthStore.getState().logout();

    if (!window.location.pathname.includes('/login')) {
        window.location.href = '/login';
    }
};

// -----------------------------
// AXIOS INTERCEPTORS
// -----------------------------

// Request interceptor - adds Authorization header
axiosInstance.interceptors.request.use(
    (config) => {
        const token = getAccessToken();
        if (token) {
            config.headers['Authorization'] = `Bearer ${token}`;
        }
        return config;
    },
    (error) => Promise.reject(error)
);

// Response interceptor - handles 401 errors with token refresh
axiosInstance.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
        const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

        // If not 401 or no original request, reject immediately
        if (error.response?.status !== 401 || !originalRequest) {
            return Promise.reject(error);
        }

        // If already retried, clear auth and redirect
        if (originalRequest._retry) {
            clearAuthAndRedirect();
            return Promise.reject(error);
        }

        const refreshToken = getRefreshToken();

        // No refresh token available, clear auth and redirect
        if (!refreshToken) {
            clearAuthAndRedirect();
            return Promise.reject(error);
        }

        // Pokud už probíhá refresh, vrátíme existující Promise
        if (refreshPromise) {
            return refreshPromise.then((token) => {
                originalRequest.headers['Authorization'] = `Bearer ${token}`;
                return axiosInstance(originalRequest);
            });
        }

        originalRequest._retry = true;

        // Vytvoříme novou refresh Promise - synchronně před jakýmkoli await
        refreshPromise = (async () => {
            const response = await refreshAxiosInstance.post<RefreshTokenResponse>(
                AUTH_REFRESH_URL,
                { refreshToken }
            );

            const { accessToken: newAccessToken, refreshToken: newRefreshToken } = response.data;

            // Update tokens in Zustand store (automatically persisted to localStorage)
            useAuthStore.getState().updateTokens({
                accessToken: newAccessToken,
                refreshToken: newRefreshToken,
                type: response.data.type,
                accessTokenExpiresIn: response.data.accessTokenExpiresIn,
                refreshTokenExpiresIn: response.data.refreshTokenExpiresIn,
            });

            return newAccessToken;
        })();

        try {
            const newToken = await refreshPromise;
            originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
            return axiosInstance(originalRequest);
        } catch (refreshError) {
            clearAuthAndRedirect();
            return Promise.reject(refreshError);
        } finally {
            refreshPromise = null;
        }
    }
);

// -----------------------------
// UDÁLOSTI
// -----------------------------

interface GetAktivniParams {
    hoursBack?: number;
    deviceToken?: string;
}

const getAktivniUdalost = (params?: GetAktivniParams) => {
    const { deviceToken, ...queryParams } = params || {};
    return handleRequest(() => axiosInstance.get(UDALOSTI_AKTIVNI_URL(deviceToken), { params: queryParams }));
};

const getAudioByPoplachId = (poplachId: number, deviceToken?: string): Promise<Blob> => {
    return handleRequest<Blob>(() => axiosInstance.get(UDALOSTI_AKTIVNI_AUDIO_URL(poplachId, deviceToken), {
        responseType: "blob",
    }));
};

const deaktivujUdalost = (id: number) => {
    return handleRequest(() => axiosInstance.post(UDALOSTI_DEAKTIVOVAT_URL(id)));
};

export interface AktivujUdalostParams {
    vytvorNovouUdalost?: boolean;
    odeslatNotifikace?: boolean;
    generovatTts?: boolean;
}

export interface AktivujUdalostResponse {
    udalost: Udalost;
    pocetOdeslanychNotifikaci: number;
    ttsVytvoreno: boolean;
    message: string;
}

const aktivujUdalost = (id: number, params?: AktivujUdalostParams) => {
    return handleRequest<AktivujUdalostResponse>(() =>
        axiosInstance.post(UDALOSTI_AKTIVOVAT_URL(id), params)
    );
};

const deleteUdalost = (id: number) => {
    return handleRequest(() => axiosInstance.delete(UDALOSTI_DELETE_URL(id)));
};

const searchUdalosti = (params: SearchUdalostiParams) => {
    const queryParams = new URLSearchParams();
    if (params.query) queryParams.append('query', params.query);
    if (params.dateFrom) queryParams.append('dateFrom', params.dateFrom);
    if (params.dateTo) queryParams.append('dateTo', params.dateTo);
    if (params.type) queryParams.append('type', params.type);
    if (params.aktivni !== undefined) queryParams.append('aktivni', String(params.aktivni));
    if (params.page !== undefined) queryParams.append('page', String(params.page));
    if (params.size !== undefined) queryParams.append('size', String(params.size));
    if (params.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);

    const queryString = queryParams.toString();
    const url = queryString ? `${UDALOSTI_ALL_URL}?${queryString}` : UDALOSTI_ALL_URL;
    return handleRequest<PagedResponseUdalost>(() => axiosInstance.get(url));
};

const getUdalostDetail = (id: number) => {
    return handleRequest<UdalostDetail>(() => axiosInstance.get(UDALOSTI_DETAIL_URL(id)));
};

export interface ResendNotifikaceParams {
    webPush?: boolean;
    fcm?: boolean;
    userIds?: number[] | null;  // null nebo undefined = všem aktivním
}

export interface ResendNotifikaceResponse {
    webPushOdeslano: number;
    fcmOdeslano: number;
    message: string;
}

const resendNotifikace = (id: number, params?: ResendNotifikaceParams) => {
    return handleRequest<ResendNotifikaceResponse>(() =>
        axiosInstance.post(UDALOSTI_RESEND_NOTIFIKACE_URL(id), params)
    );
};

export interface TestWebPushResponse {
    message: string;
    odeslano: number;
}

const testWebPush = () => {
    return handleRequest<TestWebPushResponse>(() =>
        axiosInstance.post(UDALOSTI_TEST_WEBPUSH_URL)
    );
};

const getUdalostiTypes = () => {
    return handleRequest<string[]>(() =>
        axiosInstance.get(UDALOSTI_TYPES_URL)
    );
};

const getAktivniAudio = (): Promise<Blob> => {
    return handleRequest<Blob>(() => axiosInstance.get(UDALOSTI_AKTIVNI_AUDIO_BASE_URL, {
        responseType: "blob",
    }));
};

const getAktivniPosledniHodina = () => {
    return handleRequest<Udalost[]>(() =>
        axiosInstance.get(UDALOSTI_AKTIVNI_POSLEDNI_HODINA_URL)
    );
};

const getUdalostRoute = async (id: number, deviceToken?: string): Promise<UdalostRouteResponse | null> => {
    const response = await axiosInstance.get(UDALOSTI_ROUTE_URL(id, deviceToken));
    if (response.status === 204 || !response.data) {
        return null;
    }
    return response.data;
};

const getUdalostTechnika = (id: number, deviceToken?: string) => {
    const config = deviceToken
        ? { headers: { Authorization: `DeviceToken ${deviceToken}` } }
        : undefined;
    return handleRequest(() => axiosInstance.get(UDALOSTI_TECHNIKA_URL(id, deviceToken), config));
};

const getNotifikaceLog = (id: number, deviceToken?: string) => {
    const config = deviceToken
        ? { headers: { Authorization: `DeviceToken ${deviceToken}` } }
        : undefined;
    return handleRequest(() => axiosInstance.get(UDALOSTI_NOTIFIKACE_LOG_URL(id), config));
};

// Print status and print
export interface PrintStatusResponse {
    printAvailable: boolean;
    enabledDevicesCount: number;
    alreadyQueued: boolean;
}

export interface PrintResponse {
    success: boolean;
    message: string;
}

const getPrintStatus = (id: number) => {
    return handleRequest<PrintStatusResponse>(() =>
        axiosInstance.get(UDALOSTI_PRINT_STATUS_URL(id))
    );
};

const printUdalost = (id: number) => {
    return handleRequest<PrintResponse>(() =>
        axiosInstance.post(UDALOSTI_PRINT_URL(id))
    );
};

const downloadPdf = async (id: number): Promise<void> => {
    const response = await axiosInstance.get(UDALOSTI_PDF_URL(id), {
        responseType: 'blob',
    });
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `udalost_${id}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
};

const downloadPdfPreview = async (): Promise<void> => {
    const response = await axiosInstance.get(UDALOSTI_PDF_PREVIEW_URL, {
        responseType: 'blob',
    });
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'pdf-preview.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
};

// -----------------------------
// ODPOVĚDI
// -----------------------------

const getOdpovediByPoplachId = (poplachId: number, deviceToken?: string) => {
    const config = deviceToken
        ? { headers: { Authorization: `DeviceToken ${deviceToken}` } }
        : undefined;
    return handleRequest(() => axiosInstance.get(ODPOVEDI_BY_POPLACH_ID_URL(poplachId, deviceToken), config));
};

const submitOdpoved = (poplachId: number, token: string, data: { odpoved: 'JEDU' | 'NEJEDU'; cas?: string }) => {
    return handleRequest(() =>
        axiosInstance.post(ODPOVEDI_POST_URL(poplachId, token), data)
    );
};

const getOdpovedByPoplachIdAndToken = (poplachId: number, token: string) => {
    return handleRequest(() =>
        axiosInstance.get<string | null>(ODPOVEDI_BY_POPLACH_ID_AND_TOKEN_URL(poplachId, token))
    );
};

// -----------------------------
// KONFIGURACE
// -----------------------------

const getRefreshPage = (deviceToken?: string) => {
    // Query parametr sam o sobe nestaci - refresh-page neni v LEGACY_DEVICE_TOKEN_ENDPOINTS
    // na backendu, takze v kiosk rezimu vracel 401. Autentizace jde pres header.
    const config = deviceToken
        ? { headers: { Authorization: `DeviceToken ${deviceToken}` } }
        : undefined;
    return handleRequest<Konfigurace>(() => axiosInstance.get(REFRESH_PAGE(deviceToken), config));
};

const getAllKonfigurace = () => {
    return handleRequest<Konfigurace[]>(() => axiosInstance.get(KONFIGURACE_ALL_URL));
};

const getKonfiguraceByKey = (klic: string) => {
    return handleRequest<Konfigurace>(() => axiosInstance.get(KONFIGURACE_BY_KEY_URL(klic)));
};

const getAppConfig = () => {
    return handleRequest<AppConfigResponse>(() => axiosInstance.get(APP_CONFIG_URL));
};

// NOTE: updateKonfigurace byl odstraněn - použijte AdminService.updateAdminKonfigurace (vyžaduje ADMIN práva)

// -----------------------------
// STATISTIKY
// -----------------------------

interface UdalostStatistikyParams {
    dateFrom?: string;
    dateTo?: string;
}

const getUdalostStatistiky = (params?: UdalostStatistikyParams) => {
    return handleRequest<UdalostStatistiky>(() =>
        axiosInstance.get(UDALOSTI_STATISTIKY_URL, { params })
    );
};

const getStatistikaPoplachy = (pocet = 10) => {
    return handleRequest<StatistikaPoplach[]>(() =>
        axiosInstance.get(STATISTIKA_POPLACHY_URL, { params: { pocet } })
    );
};

const getStatistika = () => {
    return handleRequest<StatistikaResponse>(() =>
        axiosInstance.get(STATISTIKA_BASE_URL)
    );
};

const getStatistikaDatum = (datum: string) => {
    return handleRequest(() =>
        axiosInstance.get(STATISTIKA_DATUM_URL(datum))
    );
};

const getOsobniStatistika = (params?: ReportFilterParams) => {
    return handleRequest<OsobniStatistikaResponse>(() =>
        axiosInstance.get(STATISTIKA_MOJE_URL, { params })
    );
};

const getStatistikaUzivatele = (id: number, params?: ReportFilterParams) => {
    return handleRequest<OsobniStatistikaResponse>(() =>
        axiosInstance.get(STATISTIKA_UZIVATEL_URL(id), { params })
    );
};

const getTymoveGrafy = (params?: ReportFilterParams & { deviceToken?: string }) => {
    return handleRequest<TymoveGrafyResponse>(() =>
        axiosInstance.get(STATISTIKA_TYM_GRAFY_URL, { params })
    );
};

// -----------------------------
// DASHBOARD
// -----------------------------

interface DashboardStatsParams {
    /** Device token pro mobilní zařízení */
    deviceToken?: string;
    /** Začátek období (YYYY-MM-DD) */
    od?: string;
    /** Konec období (YYYY-MM-DD) */
    doCasu?: string;
    /** Preset období: TODAY, WEEK, MONTH, QUARTER, YEAR */
    obdobi?: 'TODAY' | 'WEEK' | 'MONTH' | 'QUARTER' | 'YEAR';
    /** Počet top členů (default: 5) */
    limit?: number;
    /** Filtr podle typu události (např. POZAR) */
    typ?: string;
    /** Filtr podle ID uživatele */
    uzivatelId?: number;
}

const getDashboardStats = (params?: DashboardStatsParams) => {
    return handleRequest<DashboardStats>(() =>
        axiosInstance.get(DASHBOARD_STATS_URL, { params })
    );
};

// -----------------------------
// LOKACE
// -----------------------------

const searchLokace = (query: string) => {
    return handleRequest<LokaceSearchResult[]>(() =>
        axiosInstance.get(LOKACE_SEARCH_URL, { params: { q: query } })
    );
};

// -----------------------------
// JEDNOTKA
// -----------------------------

const getJednotka = () => {
    return handleRequest<JednotkaDto>(() => axiosInstance.get(JEDNOTKA_URL));
};

const updateJednotka = (data: JednotkaDto) => {
    return handleRequest<JednotkaDto>(() => axiosInstance.put(JEDNOTKA_URL, data));
};

const getJednotkaLogoUrl = (): string => {
    return `${backendURL}${JEDNOTKA_LOGO_URL}`;
};

const uploadJednotkaLogo = (file: File): Promise<void> => {
    const formData = new FormData();
    formData.append('file', file);
    return handleRequest<void>(() => axiosInstance.post(JEDNOTKA_LOGO_URL, formData));
};

const deleteJednotkaLogo = (): Promise<void> => {
    return handleRequest<void>(() => axiosInstance.delete(JEDNOTKA_LOGO_URL));
};

// -----------------------------
// REPORTS
// -----------------------------

export interface ReportFilterParams {
    dateFrom?: string;
    dateTo?: string;
    typ?: string;
    podtyp?: string;
}

const getReportSouhrn = (params?: ReportFilterParams) =>
    handleRequest<ReportSouhrn>(() => axiosInstance.get(REPORTS_SOUHRN_URL, { params }));

const getReportSouhrnClen = (params?: ReportFilterParams & { clen?: number }) =>
    handleRequest<ReportSouhrnClena>(() => axiosInstance.get(REPORTS_SOUHRN_CLEN_URL, { params }));

const getReportDetail = (params?: ReportFilterParams) =>
    handleRequest<ReportDetail>(() => axiosInstance.get(REPORTS_DETAIL_URL, { params }));

// Vytáhne název souboru z hlavičky Content-Disposition (preferuje filename*=UTF-8'').
// BE ho posílá popisný (Vykaz-cinnosti_JSDH_<od>_<do>.pdf); CORS ho vystavuje (WebConfig).
const parseDownloadFilename = (cd: string | undefined, fallback: string): string => {
    if (!cd) return fallback;
    const star = cd.match(/filename\*=UTF-8''([^;]+)/i);
    if (star && star[1]) {
        try {
            return decodeURIComponent(star[1].replace(/"/g, '').trim());
        } catch {
            /* ignore a spadni na plain filename */
        }
    }
    const plain = cd.match(/filename="?([^";]+)"?/i);
    return plain && plain[1] ? plain[1].trim() : fallback;
};

const exportReportXlsx = async (params?: ReportFilterParams): Promise<{ blob: Blob; filename: string }> => {
    const res = await axiosInstance.get(REPORTS_EXPORT_XLSX_URL, { params, responseType: 'blob' });
    return { blob: res.data, filename: parseDownloadFilename(res.headers['content-disposition'], 'report.xlsx') };
};

const exportReportPdf = async (params?: ReportFilterParams & { styl?: string }): Promise<{ blob: Blob; filename: string }> => {
    const res = await axiosInstance.get(REPORTS_EXPORT_PDF_URL, { params, responseType: 'blob' });
    return { blob: res.data, filename: parseDownloadFilename(res.headers['content-disposition'], 'report.pdf') };
};

// -----------------------------
// DEVICE TOKENS
// -----------------------------

const createDeviceToken = (data?: CreateDeviceTokenRequest) => {
    return handleRequest<DeviceTokenResponse>(() =>
        axiosInstance.post(DEVICE_TOKEN_CREATE_URL, data)
    );
};

const getDeviceTokens = (type?: 'raspberry' | 'mobile') => {
    return handleRequest<DeviceTokenResponse[]>(() =>
        axiosInstance.get(DEVICE_TOKENS_LIST_URL, { params: type ? { type } : undefined })
    );
};

const deleteDeviceToken = (tokenId: number) => {
    return handleRequest(() =>
        axiosInstance.delete(DEVICE_TOKEN_DELETE_URL(tokenId))
    );
};

const updateDeviceToken = (tokenId: number, data: UpdateDeviceTokenRequest) => {
    return handleRequest<DeviceTokenResponse>(() =>
        axiosInstance.put(DEVICE_TOKEN_UPDATE_URL(tokenId), data)
    );
};

const getDeviceTokenTheme = (token: string) => {
    return handleRequest<DeviceTokenThemeResponse>(() =>
        axiosInstance.get(DEVICE_TOKEN_THEME_URL(token))
    );
};

// -----------------------------
// POPLACH STATUS
// -----------------------------

const getPoplachStatus = () => {
    return handleRequest<AlertStatus>(() =>
        axiosInstance.get(POPLACH_STATUS_URL)
    );
};

// -----------------------------

const JsdhNotifikaceService = {
    // Události
    getAktivniUdalost,
    getAudioByPoplachId,
    deaktivujUdalost,
    aktivujUdalost,
    deleteUdalost,
    searchUdalosti,
    getUdalostDetail,
    resendNotifikace,
    testWebPush,
    getUdalostiTypes,
    getAktivniAudio,
    getAktivniPosledniHodina,
    getUdalostRoute,
    getUdalostTechnika,
    getNotifikaceLog,
    getPrintStatus,
    printUdalost,
    downloadPdf,
    downloadPdfPreview,
    // Odpovědi
    getOdpovediByPoplachId,
    submitOdpoved,
    getOdpovedByPoplachIdAndToken,
    // Konfigurace
    getRefreshPage,
    getAllKonfigurace,
    getKonfiguraceByKey,
    getAppConfig,
    // Statistiky
    getUdalostStatistiky,
    getStatistikaPoplachy,
    getStatistika,
    getStatistikaDatum,
    getOsobniStatistika,
    getStatistikaUzivatele,
    getTymoveGrafy,
    // Dashboard
    getDashboardStats,
    // Lokace
    searchLokace,
    // Jednotka
    getJednotka,
    updateJednotka,
    getJednotkaLogoUrl,
    uploadJednotkaLogo,
    deleteJednotkaLogo,
    // Reports
    getReportSouhrn,
    getReportSouhrnClen,
    getReportDetail,
    exportReportXlsx,
    exportReportPdf,
    // Device tokens
    createDeviceToken,
    getDeviceTokens,
    deleteDeviceToken,
    updateDeviceToken,
    getDeviceTokenTheme,
    // Poplach status
    getPoplachStatus,
};

export default JsdhNotifikaceService;
